import React, { useState, useEffect } from 'react';
import {
  Card, Form, Input, Button, message, Switch, InputNumber, Space, Typography, Spin, Upload
} from 'antd';
import { PictureOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;

const resolveImage = (src) => {
  if (!src) return '';
  if (src.startsWith('http') || src.startsWith('data:')) return src;
  return `${process.env.REACT_APP_API_URL || ''}${src}`;
};

const AdPopupManager = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  useEffect(() => {
    fetchPopup();
  }, []);

  const fetchPopup = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const img = data.popup_image || '';
        setImageUrl(img);
        form.setFieldsValue({
          popup_enabled: data.popup_enabled === '1' || data.popup_enabled === true,
          popup_text: data.popup_text || '',
          popup_duration_seconds: parseInt(data.popup_duration_seconds, 10) || 5,
          popup_remind_hours: parseInt(data.popup_remind_hours, 10) || 24
        });
      }
    } catch (error) {
      message.error('تعذر جلب بيانات الإعلان المنبثق');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('popup_image', file);
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings/popup-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        setImageUrl(data.popup_image);
        message.success('تم رفع الصورة بنجاح');
      } else {
        const errorData = await res.json();
        message.error(errorData.message || 'تعذر رفع الصورة');
      }
    } catch (error) {
      console.error('خطأ في رفع الصورة:', error);
      message.error('تعذر رفع الصورة');
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        popup_enabled: values.popup_enabled ? '1' : '0',
        popup_image: imageUrl || '',
        popup_text: values.popup_text || '',
        popup_duration_seconds: String(values.popup_duration_seconds || 5),
        popup_remind_hours: String(values.popup_remind_hours || 24)
      };
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        message.success('تم حفظ الإعلان المنبثق بنجاح');
      } else {
        const errorData = await res.json();
        message.error(errorData.message || 'تعذر حفظ الإعلان المنبثق');
      }
    } catch (error) {
      console.error('خطأ في حفظ الإعلان المنبثق:', error);
      message.error('تعذر الاتصال بالخادم');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
  }

  return (
    <Card
      title={
        <span>
          <PictureOutlined /> الإعلان المنبثق
        </span>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSave}>
        <Form.Item
          name="popup_enabled"
          label="إظهار / إيقاف"
          valuePropName="checked"
          extra={
            <Text type="secondary">عند التفعيل، تظهر نافذة منبثقة مع أول زيارة للزائر.</Text>
          }
        >
          <Switch />
        </Form.Item>

        <Form.Item label="صورة الإعلان">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={handleImageUpload}
            >
              <Button icon={<UploadOutlined />} loading={uploading}>
                رفع صورة الإعلان
              </Button>
            </Upload>
            {imageUrl && (
              <img
                src={resolveImage(imageUrl)}
                alt="معاينة الإعلان"
                style={{
                  maxWidth: 240,
                  maxHeight: 120,
                  borderRadius: 8,
                  objectFit: 'cover',
                  border: '1px solid #eee'
                }}
              />
            )}
          </div>
        </Form.Item>

        <Form.Item
          name="popup_text"
          label="نص الإعلان"
        >
          <TextArea
            rows={3}
            placeholder="النص الذي يظهر أسفل الصورة في النافذة المنبثقة..."
          />
        </Form.Item>

        <Form.Item
          name="popup_duration_seconds"
          label="مدة ظهور النافذة (ثواني)"
          extra={
            <Text type="secondary">المدة التي تبقى فيها النافذة ظاهرة قبل إغلاقها تلقائياً (افتراضياً 5 ثوان).</Text>
          }
        >
          <InputNumber min={3} max={60} style={{ width: 200 }} addonAfter="ثانية" />
        </Form.Item>

        <Form.Item
          name="popup_remind_hours"
          label="مدة إعادة الظهور (ساعات)"
          extra={
            <Text type="secondary">
              بعد ظهورها، لن تظهر للزائر مجدداً إلا بعد هذه المدة (افتراضياً 24 ساعة).
            </Text>
          }
        >
          <InputNumber min={1} max={720} style={{ width: 200 }} addonAfter="ساعة" />
        </Form.Item>

        <Space style={{ marginTop: 8 }}>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={saving}
          >
            حفظ الإعلان المنبثق
          </Button>
        </Space>
      </Form>
    </Card>
  );
};

export default AdPopupManager;